import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LabelsProvider } from './context/LabelsContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/globals.css';

// Pages - Existentes
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Clientes from './pages/Clientes';
import Servicios from './pages/Servicios';
import Citas from './pages/Citas';
import Reportes from './pages/Reportes';

// Pages - Nuevas ✅
import Usuarios from './pages/Usuarios';
import Sucursales from './pages/Sucursales';
import Horarios from './pages/Horarios';
import Configuracion from './pages/Configuracion';
import Profesionales from './pages/Profesionales';
import WhatsApp from './pages/WhatsApp';
import AdminDashboard from './pages/AdminDashboard';
import PlanesAdmin from './pages/PlanesAdmin';
import SuscripcionesAdmin from './pages/SuscripcionesAdmin';
import ErrorLogs from './pages/ErrorLogs';
import Checkout from './pages/Checkout';
import CheckoutConfirm from './pages/CheckoutConfirm';
import RenovarSuscripcion from './pages/RenovarSuscripcion';
import CambiarPlan from './pages/CambiarPlan';
import HistorialAcciones from './pages/HistorialAcciones';
import ReservaPublica from './pages/ReservaPublica';
import Catalogos from './pages/Catalogos';
import CatalogoPublico from './pages/CatalogoPublico';
import ReagendarCita from './pages/ReagendarCita';
import FichaCliente from './pages/FichaCliente';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DatosNegocio from './pages/DatosNegocio';
import VerificarEmail from './pages/VerificarEmail';
import PlanSuspendido from './pages/PlanSuspendido';
import CRMProspectos from './pages/CRMProspectos';
import Difusion from './pages/Difusion';
import FichaProspecto from './pages/FichaProspecto';


const RUTAS_PERMITIDAS_SUSPENDIDO = ['/', '/plan-suspendido', '/suscripcion/pagar', '/cambiar-plan', '/checkout', '/checkout/confirmar-pago', '/checkoutconfirm'];

const PrivateRoute = ({ children, permitirSuspendido = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading-screen">Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const suspendido = user?.tenant?.acceso_bloqueado ||
    ['suspendido', 'cancelado', 'vencido'].includes(user?.tenant?.estado_suscripcion);
  const esSuperAdmin = user?.rol === 'superadmin';

  if (suspendido && !esSuperAdmin && !permitirSuspendido) {
    const rutaActual = location.pathname;
    const permitida = RUTAS_PERMITIDAS_SUSPENDIDO.some(r => rutaActual === r || rutaActual.startsWith(r + '/'));
    if (!permitida) {
      return <Navigate to="/plan-suspendido" replace />;
    }
  }

  return children;
};

// Ruta raíz inteligente: superadmin → AdminDashboard, resto → Dashboard
const RootRoute = () => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) return <div className="loading-screen">Cargando...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.rol === 'superadmin') return <AdminDashboard />;
  return <Dashboard />;
};

function PublicRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
}

const AppContent = () => {
  const { theme } = useAuth();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <>
      <Routes>
        {/* Rutas públicas */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reservar/:codigo" element={<ReservaPublica />} />
        <Route path="/catalogo/:codigo" element={<CatalogoPublico />} />
        <Route path="/reagendar/:token" element={<ReagendarCita />} />
        <Route path="/verificar-email" element={<VerificarEmail />} />
        
        {/* Rutas protegidas - Principales */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/plan-suspendido" element={<PrivateRoute permitirSuspendido><PlanSuspendido /></PrivateRoute>} />
        <Route path="/clientes" element={<PrivateRoute><Clientes /></PrivateRoute>} />
        <Route path="/clientes/:id" element={<PrivateRoute><FichaCliente /></PrivateRoute>} />
        <Route path="/servicios" element={<PrivateRoute><Servicios /></PrivateRoute>} />
        <Route path="/citas" element={<PrivateRoute><Citas /></PrivateRoute>} />
        <Route path="/profesionales" element={<PrivateRoute><Profesionales /></PrivateRoute>} />
        <Route path="/reportes" element={<PrivateRoute><Reportes /></PrivateRoute>} />
        <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
        <Route path="/admin/planes" element={<PrivateRoute><PlanesAdmin /></PrivateRoute>} />
        <Route path="/admin/suscripciones" element={<PrivateRoute><SuscripcionesAdmin /></PrivateRoute>} />
        <Route path="/admin/logs" element={<PrivateRoute><ErrorLogs /></PrivateRoute>} />
        <Route path="/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} />
        <Route path="/checkout/confirmar-pago" element={<PrivateRoute><CheckoutConfirm /></PrivateRoute>} />
        <Route path="/checkoutconfirm" element={<PrivateRoute><CheckoutConfirm /></PrivateRoute>} />
        <Route path="/suscripcion/pagar" element={<PrivateRoute><RenovarSuscripcion /></PrivateRoute>} />
        <Route path="/cambiar-plan" element={<PrivateRoute><CambiarPlan /></PrivateRoute>} />
        
        {/* Rutas protegidas - Configuración */}
        <Route path="/usuarios" element={<PrivateRoute><Usuarios /></PrivateRoute>} />
        <Route path="/sucursales" element={<PrivateRoute><Sucursales /></PrivateRoute>} />
        <Route path="/horarios" element={<PrivateRoute><Horarios /></PrivateRoute>} />
        <Route path="/configuracion" element={<PrivateRoute><Configuracion /></PrivateRoute>} />
        <Route path="/datos-negocio" element={<PrivateRoute><DatosNegocio /></PrivateRoute>} />
        <Route path="/historial" element={<PrivateRoute><HistorialAcciones /></PrivateRoute>} />
        <Route path="/whatsapp" element={<PrivateRoute><WhatsApp /></PrivateRoute>} />

        {/* CRM */}
        <Route path="/crm/prospectos" element={<PrivateRoute><CRMProspectos /></PrivateRoute>} />
        <Route path="/crm/prospectos/:id" element={<PrivateRoute><FichaProspecto /></PrivateRoute>} />
        <Route path="/difusion" element={<PrivateRoute><Difusion /></PrivateRoute>} />
        {/* Catálogos */}
        <Route path="/catalogos" element={<PrivateRoute><Catalogos /></PrivateRoute>} />
      </Routes>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

function App() {
  return (
    <BrowserRouter basename="/agenda">
      <AuthProvider>
        <LabelsProvider>
          <AppContent />
        </LabelsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;